import { useCountries, countryOf, FAMILY_PALETTE, FAMILY_PALETTE_NAME } from "@/components/Country";
import { VideoPrompts } from "@/components/VideoPrompts";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { hsl } from "@/lib/color";
import { StatusBadge } from "@/components/StatusBadge";
import { ProgressTimeline } from "@/components/ProgressTimeline";
import { ChapterReader } from "@/components/ChapterReader";
import { RatingsPanel } from "@/components/RatingsPanel";
import { ToolkitPanel, SourcesPanel } from "@/components/ToolkitPanel";
import { ListingEditor } from "@/components/ListingEditor";
import { DownloadHub } from "@/components/DownloadHub";
import { UpdatePanel } from "@/components/UpdatePanel";
import { StorePublish } from "@/components/StorePublish";
import { ArtworkCard } from "@/components/ArtworkCard";
import { SocialPostCard } from "@/components/SocialPostCard";
import { IssueEditor } from "@/components/IssueEditor";
import { Button } from "@/components/ui/button";
import { LaunchProduct } from "@/components/LaunchProduct";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const TABS = [["chapters", "Chapters"], ["ratings", "Ratings"], ["toolkit", "Toolkit data"], ["sources", "Sources"], ["listing", "Store listing"]];

const DeleteButton = ({ onConfirm }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="outline" data-testid="delete-guide-button" className="rounded-none"><Trash2 className="w-4 h-4 mr-2" />Delete</Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display">Delete this guide?</AlertDialogTitle>
        <AlertDialogDescription>The PDF, toolkit and listing will be removed permanently.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid="delete-confirm-button" onClick={onConfirm}>Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default function GuideDetail() {
  const countryList = useCountries();
  const { id } = useParams();
  const nav = useNavigate();
  const [g, setG] = useState(null);
  const [pollKey, setPollKey] = useState(0);

  const load = useCallback(async () => (await api.get(`/guides/${id}`)).data, [id]);

  useEffect(() => {
    let alive = true, timer;
    const tick = async () => {
      try {
        const d = await load();
        if (!alive) return;
        setG(d);
        if (!["done", "failed"].includes(d.status) || d.art_status === "generating") timer = setTimeout(tick, 3000);
      } catch (e) {
        if (alive) toast.error(errMsg(e));
      }
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [load, pollKey]);

  const regenerate = async () => {
    try {
      await api.post(`/guides/${id}/regenerate`);
      toast.success("Regenerating guide");
      setPollKey((k) => k + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const update = async () => {
    try {
      await api.post(`/guides/${id}/update`);
      toast.success("Update started — re-researching all sources");
      setPollKey((k) => k + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/guides/${id}`); toast.success("Guide deleted"); nav("/"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!g) return <p className="eyebrow">Loading guide…</p>;
  const done = g.status === "done";
  const cc = countryOf(countryList, g.country);
  const P = g.family ? FAMILY_PALETTE : cc?.palette || { dark: "#3B1B48", accent: "#C59B27", mist: "#EFE6F3", text_muted: "#8C827A" };
  const paletteName = g.family ? FAMILY_PALETTE_NAME : cc?.palette_name;

  return (
    <div className="rise" data-testid="guide-page" style={{ "--g-dark": P.dark, "--g-dark2": P.dark2 || P.dark, "--g-accent": P.accent, "--g-mist": P.mist, "--primary": hsl(P.dark) }}>
      <div data-testid="guide-header" className="flex flex-wrap items-end justify-between gap-6 mb-12 p-6 sm:p-8 -mx-1"
        style={{ background: P.mist, borderLeft: `6px solid ${P.accent}`, borderTop: `1px solid ${P.line || P.mist}` }}>
        <div>
          <p className="eyebrow mb-3 flex flex-wrap items-center gap-x-2 gap-y-1" style={{ color: P.dark }}>
            <span data-testid="guide-series">{(cc?.series || `Small Towns of ${g.country}`) + (g.family ? " for Families" : "")}</span> · <IssueEditor guide={g} onSaved={() => setPollKey((k) => k + 1)} /> · Edition {g.edition}
            {cc && <span data-testid="guide-palette" className="ml-2 inline-flex items-center gap-1 normal-case tracking-normal text-[11px]" style={{ color: P.text_muted }}>
              <i className="w-3 h-3 inline-block" style={{ background: P.dark }} /><i className="w-3 h-3 inline-block" style={{ background: P.accent }} />{paletteName}</span>}
          </p>
          <h1 data-testid="guide-title" className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none" style={{ color: P.dark }}>{g.town}</h1>
          <div className="flex items-center gap-4 mt-4">
            <StatusBadge status={g.updating ? "updating" : g.status} testId="guide-status-badge" />
            {g.content?.tagline && <p className="font-display italic text-[#8C827A]">{g.content.tagline}</p>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {done && <StorePublish guide={g} onChange={(patch) => setG({ ...g, ...patch })} />}
          {g.status === "failed" && (
            <Button variant="outline" data-testid="regenerate-button" onClick={regenerate} className="rounded-none"><RefreshCw className="w-4 h-4 mr-2" />Regenerate</Button>
          )}
          <DeleteButton onConfirm={remove} />
        </div>
      </div>

      {g.status === "failed" && (
        <div data-testid="guide-error" className="border border-red-200 bg-red-50 text-red-800 p-5 mb-10 text-sm">{g.error}</div>
      )}

      {!done && g.updating && g.files?.pdf && (
        <div className="mb-10" data-testid="updating-notice">
          <p className="text-sm text-[var(--g-dark,#3B1B48)] mb-4">Updating to edition {g.edition + 1}. Edition {g.edition} stays available for download and in the store feed until the new edition is ready.</p>
          <DownloadHub guideId={g.id} />
        </div>
      )}

      {!done && <ProgressTimeline guide={g} />}

      {done && g.content && (
        <>
          <LaunchProduct guideId={g.id} onChanged={() => setPollKey((k) => k + 1)} />
          <UpdatePanel guide={g} onUpdate={update} onChanged={() => setPollKey((k) => k + 1)} />
          <ArtworkCard guide={g} onStarted={() => setPollKey((k) => k + 1)} />
          <SocialPostCard guide={g} onCreated={() => setPollKey((k) => k + 1)} />
          <div className="mt-10"><VideoPrompts kind="guide" refId={g.id} /></div>
          <DownloadHub guideId={g.id} />
          <Tabs defaultValue="chapters" className="mt-14">
            <TabsList className="bg-transparent border-b border-[#E2D9CC] rounded-none w-full justify-start h-auto p-0 gap-2 mb-10 overflow-x-auto overflow-y-hidden">
              {TABS.map(([v, l]) => (
                <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}
                  className="rounded-none px-4 py-3 text-[#8C827A] data-[state=active]:text-[var(--g-dark,#3B1B48)] data-[state=active]:shadow-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[var(--g-accent,#C59B27)]">{l}</TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="chapters"><ChapterReader chapters={g.content.chapters} /></TabsContent>
            <TabsContent value="ratings"><RatingsPanel c={g.content} /></TabsContent>
            <TabsContent value="toolkit"><ToolkitPanel c={g.content} rate={g.eur_aud_rate} /></TabsContent>
            <TabsContent value="sources"><SourcesPanel c={g.content} /></TabsContent>
            <TabsContent value="listing"><ListingEditor guideId={g.id} listing={g.listing} onSaved={(listing) => { setG({ ...g, listing }); setTimeout(() => setPollKey((k) => k + 1), 8000); }} /></TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
